/**
 * TemplateFormModal — create or edit a CheckInPromptTemplate.
 *
 * Authoring rules checklist (all 5 must be checked before Save is enabled):
 *  1. This is a single warm question
 *  2. Contains no clinical advice or diagnosis
 *  3. Contains no food prescription
 *  4. Contains no numbers or targets
 *  5. Reads naturally without any personalisation
 *
 * Server validates: non-empty structuralTemplate ≤300 chars, chips 2–5 items ≤40 chars each.
 * On 409 version conflict: shows inline error, does NOT close modal.
 */

import { usePortalAuth } from '@nrx/auth-ui';
import type { JSX } from 'react';
import { useState } from 'react';

import { SERVICES_BASE_URL } from '../../../lib/services-base.js';
import type { PromptTemplate, PromptTemplateCategory } from '../prompt-library-page.js';

const CATEGORY_OPTIONS: { value: PromptTemplateCategory; label: string }[] = [
  { value: 'mood', label: 'Mood' },
  { value: 'sleep', label: 'Sleep' },
  { value: 'meal', label: 'Meal' },
  { value: 'energy', label: 'Energy' },
  { value: 'resilience', label: 'Resilience' },
  { value: 'celebration', label: 'Celebration' },
  { value: 'goal_progress', label: 'Goal Progress' },
  { value: 'reflection', label: 'Weekly Reflection' },
];

const AUTHORING_RULES = [
  'This is a single warm question',
  'Contains no clinical advice or diagnosis',
  'Contains no food prescription',
  'Contains no numbers or targets',
  'Reads naturally without any personalisation',
] as const;

interface Props {
  mode: 'create' | 'edit';
  existing?: PromptTemplate;
  accessToken: string;
  onSaved: (template: PromptTemplate) => void;
  onClose: () => void;
}

export function TemplateFormModal({
  mode,
  existing,
  accessToken,
  onSaved,
  onClose,
}: Props): JSX.Element {
  void usePortalAuth; // Suppress unused import in case parent already handles auth

  const [category, setCategory] = useState<PromptTemplateCategory>(existing?.category ?? 'mood');
  const [structuralTemplate, setStructuralTemplate] = useState(existing?.structuralTemplate ?? '');
  const [chips, setChips] = useState<string[]>(existing?.chips ?? ['', '']);
  const [culturalTags, setCulturalTags] = useState((existing?.culturalTags ?? []).join(', '));
  const [clinicalTracks, setClinicalTracks] = useState((existing?.clinicalTracks ?? []).join(', '));
  const [locale, setLocale] = useState(existing?.locale ?? '');
  const [parentTemplateId, setParentTemplateId] = useState(existing?.parentTemplateId ?? '');

  // Authoring checklist — all 5 must be checked
  const [checklist, setChecklist] = useState<boolean[]>(AUTHORING_RULES.map(() => false));

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form validity
  const allChecklistDone = checklist.every(Boolean);
  const hasMinChips = chips.filter((c) => c.trim().length > 0).length >= 2;
  const formValid =
    allChecklistDone &&
    structuralTemplate.trim().length > 0 &&
    structuralTemplate.length <= 300 &&
    hasMinChips &&
    chips.every((c) => c.length <= 40);

  function handleChipChange(idx: number, value: string): void {
    setChips((prev) => {
      const next = [...prev];
      next[idx] = value;
      return next;
    });
  }

  function handleAddChip(): void {
    if (chips.length < 5) {
      setChips((prev) => [...prev, '']);
    }
  }

  function handleRemoveChip(idx: number): void {
    if (chips.length > 2) {
      setChips((prev) => prev.filter((_, i) => i !== idx));
    }
  }

  function toggleChecklist(idx: number): void {
    setChecklist((prev) => {
      const next = [...prev];
      next[idx] = !next[idx];
      return next;
    });
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!formValid) return;

    setSaving(true);
    setError(null);

    const body: Record<string, unknown> = {
      category,
      structuralTemplate: structuralTemplate.trim(),
      chips: chips.filter((c) => c.trim().length > 0),
      culturalTags: culturalTags
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      clinicalTracks: clinicalTracks
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      ...(locale.trim() !== '' && { locale: locale.trim() }),
      ...(parentTemplateId.trim() !== '' && { parentTemplateId: parentTemplateId.trim() }),
    };

    if (mode === 'edit' && existing !== undefined) {
      body['version'] = existing.version;
    }

    try {
      const url =
        mode === 'create'
          ? `${SERVICES_BASE_URL}/v1/engagement/admin/v1/prompt-library/templates`
          : `${SERVICES_BASE_URL}/v1/engagement/admin/v1/prompt-library/templates/${existing!.templateId}`;

      const res = await fetch(url, {
        method: mode === 'create' ? 'POST' : 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(body),
      });

      if (res.status === 409) {
        setError('Someone else edited this template — please reload and try again.');
        return;
      }

      if (!res.ok) {
        const errBody = (await res.json()) as { detail?: string };
        setError(`Save failed (${res.status}): ${errBody.detail ?? 'Unknown error'}`);
        return;
      }

      const saved = (await res.json()) as PromptTemplate;
      onSaved(saved);
    } catch {
      setError('Unable to connect to API.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900">
            {mode === 'create' ? 'Add Template' : 'Edit Template'}
          </h2>

          <form onSubmit={(e) => void handleSubmit(e)} className="mt-4 space-y-4">
            {/* Category */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as PromptTemplateCategory)}
                className="w-full border border-gray-300 rounded-md text-sm px-3 py-1.5"
              >
                {CATEGORY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Structural template */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Structural Template{' '}
                <span
                  className={structuralTemplate.length > 300 ? 'text-red-600' : 'text-gray-400'}
                >
                  ({structuralTemplate.length}/300)
                </span>
              </label>
              <textarea
                value={structuralTemplate}
                onChange={(e) => setStructuralTemplate(e.target.value)}
                rows={3}
                maxLength={300}
                required
                placeholder="How are you feeling this morning?"
                className="w-full border border-gray-300 rounded-md text-sm px-3 py-2 resize-none"
              />
            </div>

            {/* Chips */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Response Chips (2–5, each ≤40 chars)
              </label>
              {chips.map((chip, idx) => (
                <div key={idx} className="flex gap-2 mb-1">
                  <input
                    type="text"
                    value={chip}
                    onChange={(e) => handleChipChange(idx, e.target.value)}
                    maxLength={40}
                    placeholder={`Chip ${idx + 1}`}
                    className="flex-1 border border-gray-300 rounded-md text-sm px-3 py-1"
                  />
                  {chips.length > 2 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveChip(idx)}
                      className="text-xs text-gray-400 hover:text-red-500"
                      aria-label={`Remove chip ${idx + 1}`}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              {chips.length < 5 && (
                <button
                  type="button"
                  onClick={handleAddChip}
                  className="text-xs text-indigo-600 hover:text-indigo-800 mt-1"
                >
                  + Add chip
                </button>
              )}
            </div>

            {/* Cultural tags */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Cultural Tags (comma-separated, optional)
              </label>
              <input
                type="text"
                value={culturalTags}
                onChange={(e) => setCulturalTags(e.target.value)}
                placeholder="e.g. hispanic, south-asian"
                className="w-full border border-gray-300 rounded-md text-sm px-3 py-1.5"
              />
            </div>

            {/* Clinical tracks */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Clinical Tracks (comma-separated, optional)
              </label>
              <input
                type="text"
                value={clinicalTracks}
                onChange={(e) => setClinicalTracks(e.target.value)}
                placeholder="e.g. diabetes, CKM"
                className="w-full border border-gray-300 rounded-md text-sm px-3 py-1.5"
              />
            </div>

            {/* Locale */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Locale (optional — BCP 47, e.g. "es")
              </label>
              <input
                type="text"
                value={locale}
                onChange={(e) => setLocale(e.target.value)}
                placeholder="es"
                className="w-40 border border-gray-300 rounded-md text-sm px-3 py-1.5"
              />
            </div>

            {/* Parent template ID (shown only when locale is set) */}
            {locale.trim() !== '' && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Parent Template ID (required for locale variants)
                </label>
                <input
                  type="text"
                  value={parentTemplateId}
                  onChange={(e) => setParentTemplateId(e.target.value)}
                  placeholder="ULID of English base template"
                  className="w-full border border-gray-300 rounded-md text-sm px-3 py-1.5"
                />
              </div>
            )}

            {/* Authoring rules checklist */}
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-xs font-semibold text-amber-900 mb-2">
                Authoring Rules — confirm all before saving:
              </p>
              {AUTHORING_RULES.map((rule, idx) => (
                <label key={idx} className="flex items-start gap-2 mb-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checklist[idx] ?? false}
                    onChange={() => toggleChecklist(idx)}
                    className="mt-0.5"
                  />
                  <span className="text-xs text-amber-800">{rule}</span>
                </label>
              ))}
            </div>

            {/* Error */}
            {error !== null && <p className="text-sm text-red-600">{error}</p>}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!formValid || saving}
                className="px-4 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
